import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type { AspectRollup } from './gap-analysis.utils';
import { GapLegend } from './gap-legend';

interface Props {
  rollups: AspectRollup[];
  /** Categories currently filtered on, so an active bar reads as pressed. */
  selectedCategories: string[];
  /** Toggles a category in the filter — the chart doubles as a control. */
  onToggleCategory: (category: string) => void;
  /** False when the search panel is absent, so the vertical divider that would
   *  separate the two doesn't dangle at the edge of a full-width panel. */
  hasSibling: boolean;
}

/**
 * How many attributes enrichment added in each category, ranked so the categories
 * that gained the most sit at the top. Aspects the retailer left entirely empty
 * show no leading segment, which is what makes them read as gaps.
 */
export function GapCoveragePanel({
  rollups,
  hasSibling,
  selectedCategories,
  onToggleCategory,
}: Props) {
  const ranked = [...rollups].sort(
    (a, b) => b.added.length - a.added.length || a.aspect.localeCompare(b.aspect),
  );
  const scale = Math.max(...ranked.map((rollup) => rollup.had.length + rollup.added.length), 1);
  const emptyBefore = rollups.filter((rollup) => rollup.had.length === 0).length;
  // With no baseline anywhere there is no "already in the listing" bar to
  // label — keeping it in the legend advertises a series the chart never draws.
  const anyBaseline = emptyBefore < rollups.length;

  const subtitle = anyBaseline
    ? `${String(emptyBefore)} of ${String(rollups.length)} categories were empty before enrichment.`
    : `All ${String(rollups.length)} categories were empty before enrichment.`;

  return (
    <div
      className={cn('min-w-0 border-b p-5', hasSibling && 'lg:border-r')}
      data-testid="gap-analysis-coverage-card"
    >
      <h3
        className="text-foreground text-[13px] leading-5 font-semibold"
        data-testid="gap-analysis-coverage-title"
      >
        New attributes by category
      </h3>
      <p
        className="text-muted-foreground mt-0.5 mb-4 text-xs"
        data-testid="gap-analysis-coverage-subtitle"
      >
        {subtitle}
      </p>

      <div className="flex flex-col gap-[7px]" data-testid="gap-analysis-coverage-list">
        {ranked.map((rollup) => {
          const isSelected = selectedCategories.includes(rollup.aspect);
          const summary = [
            `${String(rollup.added.length)} new`,
            rollup.had.length > 0
              ? `${String(rollup.had.length)} already there before enrichment`
              : 'this category was empty before enrichment',
          ].join(' · ');

          return (
            // The whole row is the control — one tooltip on the button rather
            // than one per segment, which would nest interactive elements.
            <Tooltip key={rollup.aspect}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-pressed={isSelected}
                  data-testid={`gap-analysis-coverage-category-button-${rollup.aspect}`}
                  onClick={() => {
                    onToggleCategory(rollup.aspect);
                  }}
                  className={cn(
                    'grid cursor-pointer grid-cols-[minmax(0,116px)_minmax(0,1fr)_auto] items-center gap-3 rounded-md px-1 py-0.5 text-left transition-colors',
                    'focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none',
                    // Hover has to deepen the selected state, not lighten it —
                    // a shared `hover:bg-muted/60` made a selected row look less
                    // selected the moment the pointer landed on it.
                    isSelected
                      ? 'bg-primary/10 ring-primary/40 hover:bg-primary/15 ring-1'
                      : 'hover:bg-muted/60',
                  )}
                >
                  <span
                    className={cn(
                      'truncate text-right text-xs capitalize',
                      isSelected ? 'text-foreground font-medium' : 'text-muted-foreground',
                    )}
                  >
                    {rollup.aspect}
                  </span>
                  <span className="flex h-[17px] items-center gap-0.5">
                    {rollup.had.length > 0 ? (
                      <span
                        className="bg-muted-foreground/15 h-[17px] min-w-[2px] rounded-sm border"
                        style={{ width: `${String((rollup.had.length / scale) * 100)}%` }}
                      />
                    ) : null}
                    {rollup.added.length > 0 ? (
                      <span
                        className="bg-success h-[17px] min-w-[2px] rounded-sm"
                        style={{ width: `${String((rollup.added.length / scale) * 100)}%` }}
                      />
                    ) : null}
                  </span>
                  <span
                    className="text-success min-w-[22px] text-xs font-semibold tabular-nums"
                    data-testid={`gap-analysis-coverage-category-value-${rollup.aspect}`}
                  >
                    +{rollup.added.length}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <span className="capitalize">{rollup.aspect}</span> — {summary}.{' '}
                {isSelected ? 'Click to clear this filter.' : 'Click to filter by this category.'}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      <GapLegend
        testId="gap-analysis-coverage-legend"
        className="mt-3"
        items={[
          ...(anyBaseline ? ([{ tone: 'had', label: 'Already there' }] as const) : []),
          { tone: 'added', label: 'New' },
        ]}
      />
    </div>
  );
}
